import axios from 'axios'

// In-memory access token (application-wide)
let inMemoryAccessToken = null
let refreshPromise = null
let refreshSubscribers = []
let refreshFailureHandler = null
let authErrorHandler = null
let lastRefreshFailedAt = 0

// Base URL should point to API root (frontend env should set VITE_API_BASE_URL=http://localhost:8000/api/v1)
const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';
const API_TIMEOUT = parseInt(import.meta.env.VITE_API_TIMEOUT, 10) || 0;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  timeout: API_TIMEOUT,
})

const rawApiNoInterceptors = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  timeout: API_TIMEOUT,
})

export function setAccessToken(token) {
  inMemoryAccessToken = token
  if (token) {
    localStorage.setItem('access_token', token)
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`
  } else {
    localStorage.removeItem('access_token')
    delete api.defaults.headers.common['Authorization']
  }
}

export function setRefreshFailureHandler(fn) {
  refreshFailureHandler = fn
}

export function setAuthErrorHandler(fn) {
  authErrorHandler = fn
}

export function resetRefreshCooldown() {
  lastRefreshFailedAt = 0
}

function subscribeRefresh(cb) {
  refreshSubscribers.push(cb)
}

function notifyRefreshSubscribers(token, err = null) {
  refreshSubscribers.forEach((cb) => cb(token, err))
  refreshSubscribers = []
}

async function performRefresh() {
  // If a refresh is already in-flight, reuse that exact promise
  if (refreshPromise) {
    return refreshPromise
  }

  // If a refresh failed very recently (< 4 seconds ago), prevent rapid cascading calls
  const now = Date.now()
  if (now - lastRefreshFailedAt < 4000) {
    const cachedErr = new Error('Token refresh recently failed. Please try again.')
    cachedErr.isCoolingDown = true
    return Promise.reject(cachedErr)
  }

  const storedRefreshToken = localStorage.getItem('refresh_token')
  if (!storedRefreshToken) {
    localStorage.removeItem('access_token')
    localStorage.removeItem('user')
    const noTokenErr = new Error('No refresh token found')
    if (typeof refreshFailureHandler === 'function') refreshFailureHandler(noTokenErr)
    throw noTokenErr
  }

  refreshPromise = (async () => {
    try {
      const res = await rawApiNoInterceptors.post('/auth/refresh', { refresh_token: storedRefreshToken })
      const newAccess = res?.data?.access_token || null
      const returnedRefresh = res?.data?.refresh_token || null

      if (newAccess) {
        setAccessToken(newAccess)
      }
      if (returnedRefresh) {
        localStorage.setItem('refresh_token', returnedRefresh)
      }

      // Keep stored_accounts in localStorage in sync with refreshed tokens immediately
      try {
        const rawAccounts = localStorage.getItem('stored_accounts')
        const activeIdx = parseInt(localStorage.getItem('active_account_index') || '0', 10)
        if (rawAccounts) {
          const accs = JSON.parse(rawAccounts)
          const idx = isNaN(activeIdx) ? 0 : activeIdx
          if (accs[idx]) {
            if (newAccess) accs[idx].access_token = newAccess
            if (returnedRefresh) accs[idx].refresh_token = returnedRefresh
            localStorage.setItem('stored_accounts', JSON.stringify(accs))
          }
        }
      } catch (_) {}

      notifyRefreshSubscribers(newAccess, null)
      lastRefreshFailedAt = 0
      return newAccess
    } catch (err) {
      lastRefreshFailedAt = Date.now()
      const status = err?.response?.status
      const isExplicitAuthFailure = status === 401 || status === 403

      if (isExplicitAuthFailure) {
        localStorage.removeItem('access_token')
        localStorage.removeItem('refresh_token')
        inMemoryAccessToken = null
        delete api.defaults.headers.common['Authorization']
        notifyRefreshSubscribers(null, err)
        if (typeof refreshFailureHandler === 'function') refreshFailureHandler(err)
      } else {
        console.warn('Refresh request failed due to server/network error; keeping session intact:', err?.message || err)
        notifyRefreshSubscribers(null, err)
      }

      if (typeof authErrorHandler === 'function') {
        authErrorHandler(err, 'refresh')
      }

      throw err
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

// Attach Authorization header if we have a token (in-memory or in localStorage)
api.interceptors.request.use(
  (config) => {
    // Cloudflare Cache Busting Mitigation: append _t parameter to all GET requests
    if (config.method && config.method.toLowerCase() === 'get') {
      config.params = config.params || {}
      if (!config.params._t) {
        config.params._t = Date.now()
      }
    }

    const token = inMemoryAccessToken || localStorage.getItem('access_token')
    if (token) {
      config.headers = config.headers || {}
      // Check for existing Authorization header in a case-insensitive manner safely
      const hasAuth = (typeof config.headers.has === 'function' && config.headers.has('Authorization')) ||
                      Object.keys(config.headers).some(k => k.toLowerCase() === 'authorization');
      
      // Only attach if not explicitly provided by the request
      if (!hasAuth) {
        if (typeof config.headers.set === 'function') {
          config.headers.set('Authorization', `Bearer ${token}`);
        } else {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor handles 401 by attempting a single refresh and retrying the original request
api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config
    if (!originalRequest || originalRequest._retry || originalRequest.url?.includes('/auth/logout') || originalRequest.url?.includes('/auth/refresh')) {
      return Promise.reject(error)
    }

    const status = error.response?.status
    if (status === 401) {
      originalRequest._retry = true
      try {
        const newToken = await performRefresh()
        if (newToken) {
          originalRequest.headers = originalRequest.headers || {}
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          return api(originalRequest)
        } else {
          delete originalRequest.headers?.Authorization
          return Promise.reject(error)
        }
      } catch (err) {
        return Promise.reject(err)
      }
    }

    return Promise.reject(error)
  }
)

// Expose a helper to trigger refresh manually (AuthContext will call this during initialization)
export async function requestRefresh() {
  return performRefresh()
}

export default api

